"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { Button, Card, Spinner, inputClass } from "@/components/ui";
import { CostSettingsEditor } from "@/components/cost-settings-editor";
import { costSettings, type CostSettings } from "@/lib/cost-model";
import { ConsumptionRecords } from "@/components/consumption-records";

interface Pricing {
  actualInputPerMillion: number;
  actualOutputPerMillion: number;
  markupPercent: number;
  customerInputPerMillion: number;
  customerOutputPerMillion: number;
  seedanceNoVideoPerMillion: number;
  seedanceWithVideoPerMillion: number;
  heygenPerMinute: number;
  videoMarkupPercent: number;
  minimumVideoPriceCents: number;
  updatedAt: string;
  operatingCosts?: CostSettings;
}

export default function TokenPricingPage() {
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [tab, setTab] = useState<"pricing" | "records">("pricing");
  const [form, setForm] = useState({
    input: "", output: "", markup: "", seedanceNoVideo: "",
    seedanceWithVideo: "", heygen: "", videoMarkup: "", minimumVideo: "",
  });
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [operatingCosts, setOperatingCosts] = useState<CostSettings>(() => costSettings(null));

  const load = useCallback(async () => {
    try {
      const value = await adminFetch<Pricing>("/api/admin/token-pricing");
      setPricing(value);
      setOperatingCosts(costSettings(value.operatingCosts));
      setForm({
        input: String(value.actualInputPerMillion),
        output: String(value.actualOutputPerMillion),
        markup: String(value.markupPercent),
        seedanceNoVideo: String(value.seedanceNoVideoPerMillion),
        seedanceWithVideo: String(value.seedanceWithVideoPerMillion),
        heygen: String(value.heygenPerMinute),
        videoMarkup: String(value.videoMarkupPercent),
        minimumVideo: String(value.minimumVideoPriceCents / 100),
      });
      setNeedKey(false);
    } catch (error) {
      if (error instanceof AdminAuthError) setNeedKey(true);
      else setNotice(error instanceof Error ? error.message : "Unable to load pricing");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    setBusy(true);
    setNotice(null);
    try {
      const value = await adminFetch<Pricing>("/api/admin/token-pricing", {
        method: "PUT",
        body: JSON.stringify({
          actualInputPerMillion: Number(form.input),
          actualOutputPerMillion: Number(form.output),
          markupPercent: Number(form.markup),
          seedanceNoVideoPerMillion: Number(form.seedanceNoVideo),
          seedanceWithVideoPerMillion: Number(form.seedanceWithVideo),
          heygenPerMinute: Number(form.heygen),
          videoMarkupPercent: Number(form.videoMarkup),
          minimumVideoPriceCents: Math.round(Number(form.minimumVideo) * 100),
          operatingCosts,
        }),
      });
      setPricing(value);
      setNotice("Pricing saved. New quotes use these costs. Existing job charges, retries and refunds keep their originally confirmed price.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Save failed");
    } finally { setBusy(false); }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!pricing) return <div className="grid min-h-[60vh] place-items-center">{notice ? <div role="alert">{notice}<Button onClick={() => { setNotice(null); void load(); }}>Retry</Button></div> : <Spinner />}</div>;
  const input = Number(form.input) || 0;
  const output = Number(form.output) || 0;
  const multiplier = 1 + (Number(form.markup) || 0) / 100;

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Costs & pricing</h1>
      <p className="mt-2 text-sm text-muted">Control internal API cost and the markup used for customer-facing estimates.</p>
      <div className="mt-5 flex gap-3"><Button onClick={() => setTab("pricing")} disabled={tab === "pricing"}>Calculator & pricing</Button><Button onClick={() => setTab("records")} disabled={tab === "records"}>Consumption records</Button></div>
      {tab === "records" ? <ConsumptionRecords /> : <>
      {notice && <Card className="mt-5 px-4 py-3 text-sm text-muted">{notice}</Card>}
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="font-medium">LLM provider cost rates</h2>
          <p className="mt-1 text-xs text-muted">Configured USD per 1 million tokens, not a live invoice. Never shown to customers.</p>
          <label className="mt-5 block text-xs text-muted">Input tokens</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.01" value={form.input} onChange={(e) => setForm({ ...form, input: e.target.value })} />
          <label className="mt-4 block text-xs text-muted">Output tokens</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.01" value={form.output} onChange={(e) => setForm({ ...form, output: e.target.value })} />
        </Card>
        <Card className="p-6">
          <h2 className="font-medium">Customer markup</h2>
          <p className="mt-1 text-xs text-muted">Applied on top of actual cost.</p>
          <label className="mt-5 block text-xs text-muted">Markup percentage</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" max="10000" step="1" value={form.markup} onChange={(e) => setForm({ ...form, markup: e.target.value })} />
          <div className="mt-5 rounded-xl bg-white/[0.05] p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted">Customer input rate</span><span>${(input * multiplier).toFixed(4)} / M</span></div>
            <div className="mt-2 flex justify-between"><span className="text-muted">Customer output rate</span><span>${(output * multiplier).toFixed(4)} / M</span></div>
          </div>
        </Card>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="font-medium">Video provider cost</h2>
          <p className="mt-1 text-xs text-muted">Internal USD cost. These rates are never shown to customers.</p>
          <label className="mt-5 block text-xs text-muted">Seedance · no input video / 1M tokens</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.1" value={form.seedanceNoVideo} onChange={(e) => setForm({ ...form, seedanceNoVideo: e.target.value })} />
          <label className="mt-4 block text-xs text-muted">Seedance · with input video / 1M tokens</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.1" value={form.seedanceWithVideo} onChange={(e) => setForm({ ...form, seedanceWithVideo: e.target.value })} />
          <label className="mt-4 block text-xs text-muted">HeyGen / minute</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.1" value={form.heygen} onChange={(e) => setForm({ ...form, heygen: e.target.value })} />
        </Card>
        <Card className="p-6">
          <h2 className="font-medium">Video customer price</h2>
          <p className="mt-1 text-xs text-muted">Calculated from duration and provider cost at confirmation time.</p>
          <label className="mt-5 block text-xs text-muted">Video markup percentage</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" max="10000" step="1" value={form.videoMarkup} onChange={(e) => setForm({ ...form, videoMarkup: e.target.value })} />
          <label className="mt-4 block text-xs text-muted">Minimum customer price / video (USD)</label>
          <input className={`${inputClass} mt-2`} type="number" min="0" step="0.01" value={form.minimumVideo} onChange={(e) => setForm({ ...form, minimumVideo: e.target.value })} />
          <div className="mt-5 rounded-xl bg-white/[0.05] p-4 text-xs leading-5 text-muted">
            Markup applies to the full modeled cost. Payment fees are grossed up from the selling price; the minimum still applies. Existing jobs are not repriced.
          </div>
        </Card>
      </div>
      <CostSettingsEditor value={operatingCosts} onChange={(key, value) => setOperatingCosts(current => ({ ...current, [key]: value }))} markup={Number(form.videoMarkup)} minimumCents={Math.round(Number(form.minimumVideo) * 100)} inputRate={input} outputRate={output} />
      <Button className="mt-5" disabled={busy} onClick={() => void save()}>{busy ? <><Spinner /> Saving…</> : "Save pricing"}</Button>
      </>}
    </div>
  );
}
